import React, { useEffect, useState } from "react";
import { Input } from "./ui/input";
import { FileUpload } from "./ui/file-upload";
import { getPublicUrl, getSignedUrl } from "~/actions/upload-helpers";
import { Button } from "./ui/button";
import {
  Archive,
  Download,
  File,
  FileSpreadsheet,
  FileText,
  X,
} from "lucide-react";

export const uploadFileToGCS = (
  signedUrl: string,
  file: File,
  onProgress: (loaded: number, total: number) => void,
) => {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signedUrl);
    xhr.setRequestHeader("Content-Type", file.type);

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    });

    xhr.onreadystatechange = () => {
      if (4 === xhr.readyState) {
        if (200 === xhr.status) {
          resolve();
        } else {
          reject(new Error("Failed to upload file."));
        }
      }
    };

    xhr.onerror = () => {
      reject(new Error("An error occurred during the upload."));
    };

    xhr.send(file);
  });
};

interface FileInputProps {
  name: string;
  type: "text" | "email" | "tel" | "password" | "url" | "date";
  label?: string;
  placeholder?: string;
  value: string | undefined | null;
  error?: unknown;
  required?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  onInput?: (event: React.FormEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  accept?: string;
  maxSize?: number;
  recommendedDimensions?: { width: number; height: number };
  uploadLabel?: string;
  previewType?: "image" | "none";
}

export const FileInput = ({
  value = "",
  name,
  ref: propRef,
  onChange,
  onInput,
  onBlur,
  accept = "image/*",

  // 5MB default
  maxSize = 5 * 1024 * 1024,

  recommendedDimensions,
  uploadLabel,
  previewType = "image",
  ...rest
}: FileInputProps) => {
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(value?.toString() ?? "");
  const [fileName, setFileName] = useState<string>("");
  const [fileType, setFileType] = useState<string>("");

  const getFileIcon = (type: string) => {
    if (
      type.includes("spreadsheet") ||
      type.includes("excel") ||
      type.includes("xlsx") ||
      type.includes("xls")
    ) {
      return <FileSpreadsheet className="h-6 w-6" />;
    }
    if (type.includes("pdf") || type.includes("text") || type.includes("doc")) {
      return <FileText className="h-6 w-6" />;
    }
    if (
      type.includes("zip") ||
      type.includes("archive") ||
      type.includes("compressed")
    ) {
      return <Archive className="h-6 w-6" />;
    }
    return <File className="h-6 w-6" />;
  };

  const getFileTypeLabel = (type: string) => {
    if (
      type.includes("spreadsheet") ||
      type.includes("excel") ||
      type.includes("xlsx")
    ) {
      return "Spreadsheet";
    }
    if (type.includes("pdf")) {
      return "PDF Document";
    }
    if (type.includes("doc")) {
      return "Word Document";
    }
    if (type.includes("zip") || type.includes("archive")) {
      return "Archive";
    }
    if (type.includes("text")) {
      return "Text Document";
    }
    return "File";
  };

  useEffect(() => {
    if (value) {
      setPreviewUrl(value.toString());
      // Extract filename from URL
      try {
        const url = new URL(value.toString());
        const pathSegments = url.pathname.split("/");
        setFileName(decodeURIComponent(pathSegments[pathSegments.length - 1]));
      } catch {
        // If URL parsing fails, just use the value as filename
        setFileName(value.toString());
      }
    }
  }, [value]);

  const handleFilesChange = async (files: File[]) => {
    if (0 === files.length) {
      return;
    }
    const file = files[0];

    setIsUploading(true);
    setErrorMessage(null);
    setProgress(0);
    setFileName(file.name);
    setFileType(file.type);

    try {
      const { name: fileName, type } = file;
      const { signedUrl, blobName } = await getSignedUrl({
        data: { filename: fileName, contentType: type },
      });

      await uploadFileToGCS(signedUrl, file, (loaded, total) => {
        setProgress(Math.round((loaded / total) * 100));
      });

      const publicUrl = await getPublicUrl({ data: { blobName } });
      setPreviewUrl(publicUrl);

      // Call onChange with the new URL
      if (onChange) {
        const syntheticEvent = {
          target: { value: publicUrl, name },
          currentTarget: { value: publicUrl, name },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    } catch {
      // Show user-friendly error message
      setErrorMessage("Failed to upload file.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setPreviewUrl("");
    setFileName("");
    setFileType("");

    // Call onChange with empty value
    if (onChange) {
      const syntheticEvent = {
        target: { value: "", name },
        currentTarget: { value: "", name },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  return (
    <div>
      {previewUrl && (
        <div className="mb-2 rounded border border-solid border-gray-200 p-2">
          <div className="mb-2 flex items-center justify-between">
            <Button
              onClick={handleClear}
              variant="destructive-ghost"
              size="icon-sm"
              aria-label="Clear file"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {"image" === previewType ? (
            <img
              width="1024"
              height="1024"
              src={previewUrl}
              alt="Preview"
              className="max-h-[200px] w-full object-contain"
            />
          ) : (
            <div className="flex items-center gap-3 rounded bg-gray-50 p-3">
              <div className="text-brand-700">
                {getFileIcon(fileType || fileName)}
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="truncate text-sm font-medium" title={fileName}>
                  {fileName}
                </p>
                <p className="text-text-tertiary text-xs">
                  {getFileTypeLabel(fileType || fileName)}
                </p>
              </div>
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-700 hover:text-brand-800"
                aria-label="Download file"
              >
                <Download className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>
      )}
      <Input
        name={name}
        value={value || ""}
        placeholder={rest.placeholder || "Enter file URL"}
        className="mb-2"
        onChange={onChange}
        onInput={onInput}
        onBlur={onBlur}
        ref={propRef}
        {...rest}
        type="hidden"
      />
      <div className="relative">
        <FileUpload
          onChange={handleFilesChange}
          isUploading={isUploading}
          progress={progress}
          accept={accept}
          maxSize={maxSize}
          recommendedDimensions={recommendedDimensions}
          label={uploadLabel}
        />
      </div>
      {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
    </div>
  );
};

// For backward compatibility
export const ImageInput = FileInput;
